import React, { useState, useContext, useEffect, useRef, useMemo, useId } from "react";
import { QuestionProps } from "./Props";
import { useLocalizedStrings } from "../Localization";
import Button from "react-bootstrap/Button";
import { get_deployment, usePost } from "../API";
import { Alert } from "react-bootstrap";
import { SpacedSpinner } from "../Util"
import { ConfigurationContext } from "../Context";
import { getCardIdFromTargetField } from "../QuestionUtil";
import { CardManagement as CardManagementType } from '@aidkitorg/types/lib/survey';
import { useToast } from "@aidkitorg/component-library";
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useAsyncEffect } from "../Hooks/AsyncEffect";

type DetailProps = {
  left: string,
  right: string | any
}
function Detail(props: DetailProps) {
  return <div style={{"display": "flex", justifyContent: "space-between", marginTop: "3px"}}>
    <b>{props.left}</b>
    <div style={{"textAlign": "right"}}>{props.right}</div>
  </div>
}

function CardStatus(props: QuestionProps & {
  cardStatus: {
    error?: string,
    status?: string,
    address: {
      address1?: string,
      address2?: string,
      city?: string,
      state?: string,
      zipcode?: string
    },
    firstName?: string,
    lastName?: string,
    amount?: string,
    registered?: boolean,
    cardNumber?: string,
  },
}
) {
  const L = useLocalizedStrings();
  const { cardStatus } = props;

  const cardIdField = getCardIdFromTargetField(props['Target Field']);
  const targetField = cardIdField.replace('_id','');

  const component = JSON.parse(props.Metadata || '{}') as CardManagementType;

  async function removeCard() {
    if (!window.confirm(L.card.are_you_sure)) {
      return;
    }
    props.setInfoKey(targetField + '_id', '', true, false);
    props.setInfoKey(targetField + '_serial', '', true, false);
    props.setInfoKey(targetField + '_last4', '', true, false);
  }

  if (!cardStatus || cardStatus.error || !cardStatus.status) {
    return <>{L.applicant.loading}</>
  } else {
    return <Alert variant={
      ['Pre-Active', 'pre-active', 'processing'].includes(cardStatus.status)
        ? "warning"
        : (cardStatus.status === 'Active' || cardStatus.status === 'active' ? "success" : 'danger')}>
      <h3>{L.card.associated}</h3>
      {/* TODO: do we want to include more instructions here for USBank? */}
      {cardStatus.status === 'Pre-Active' &&
                <><b>{L.card.not_yet_active}</b><br /><br /></>}
      <Detail left={cardStatus.registered ? "Registered To: " : ""} right={cardStatus.firstName ? cardStatus.firstName + " " + cardStatus.lastName : ""} />
      <Detail left={L.card.status} right={cardStatus.status} />
      {cardStatus.amount && <Detail left={L.card.balance_no_dollar} right={'$' + cardStatus.amount.substring(0, cardStatus.amount.length - 2) + "." +
                cardStatus.amount.substring(cardStatus.amount.length - 2)} />}
      {props.info[targetField + '_serial'] && <Detail left={L.card.serial_no} right={props.info[targetField + '_serial']} />}
      {props.info[targetField + '_last4'] && <Detail left={L.card.last_4} right={props.info[targetField + '_last4']} />}
      {/* cardNumber does not show entire number - most digits are XXXX */}
      {cardStatus.cardNumber && <Detail left={'Card Number:'} right={cardStatus.cardNumber} />}
      {cardStatus.address && <Detail left={L.card.address} right={
        <>
          {cardStatus.address.address1}<br />
          {cardStatus.address.address2 && <>{cardStatus.address.address2}<br /></>}
          {cardStatus.address.city + ", " + cardStatus.address.state + " " + cardStatus.address.zipcode}<br />
        </>} />}
      {(props["Additional Options"] || []).indexOf('View Only') === -1 &&
                <div style={{textAlign: "right"}}><Button variant="link" onClick={removeCard}>{L.card.remove}</Button></div>}
    </Alert>;
  }
}

function ChipCardStatus(props: QuestionProps) {
  const getCardDetails = usePost('/usio/card_details');
  const getGiveCardDetails = usePost('/givecard/card_details');
  const getUSBankCardDetails = usePost('/usbank/card_details');
  const config = useContext(ConfigurationContext);
  const component = JSON.parse(props.Metadata || '{}') as CardManagementType;

  const [cardStatus, setCardStatus] = useState(null as any);
  const fetching = useRef(false);
  // GiveCard doesn't do anything special with its ID field, 
  // so no need to attempt to figure out some ID field
  const targetField = component.cardType === 'GiveCard' ? props['Target Field']! : getCardIdFromTargetField(props['Target Field']);
  const [retry, setRetry] = useState(0);
  const retryTimer = useRef(null as any);

  useEffect(() => {
    if (fetching.current || cardStatus) return;

    (async () => {
      fetching.current = true;
      let detailsResult;
      if (component.cardType === 'GiveCard') {
        detailsResult =  await getGiveCardDetails({
          cardId: props.info[targetField],
          targetField,
          returnPII: !!config?.user?.uid
        })
      } else if (typeof component.cardType === 'object' && component.cardType.kind === 'USBank') {
        detailsResult = await getUSBankCardDetails({ cardId: props.info[targetField] });
        if ((detailsResult as any).value) {
          const formatted = {
            status: (detailsResult as any).value.card?.status,
            details: {
              cardId: JSON.stringify((detailsResult as any).value.card?.id || ''),
              cardNumber: (detailsResult as any).value.card?.cardNumber,
            }
          }
          detailsResult = {
            value: formatted
          };
        }
      // if no cardType is provided, we assume Usio
      } else {
        detailsResult = await getCardDetails({
          cardId: props.info[targetField],
          targetField,
          returnPII: !!config?.user?.uid
        });
      }
      if (detailsResult) setCardStatus((detailsResult as any).value || (detailsResult as any).error);
      fetching.current = false;
      /*
            if (!(detailsResult as any).value) {
                if (retryTimer.current) clearTimeout(retryTimer.current);
                retryTimer.current = setTimeout(() => setRetry(retry + 1), 3000);
            }
            */
    })();
  }, [props.info, retry]);

  return <CardStatus {...props} cardStatus={{
    amount: cardStatus?.status?.balance,
    status: cardStatus?.status?.status || cardStatus?.status,
    ...(cardStatus?.details && { ...cardStatus.details })
  }} />
}

function ChipCardAssociation(props: QuestionProps) {
  const L = useLocalizedStrings();

  const [cardId, setCardId] = useState('');
  const [last4, setLast4] = useState('');
  const [pin, setPin] = useState('');
  const [pinAgain, setPinAgain] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [readyToSubmit, setReadyToSubmit] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { toast } = useToast();

  const activateUsioCard = usePost('/usio/activate');
  const checkGiveCardLast4 = usePost('/givecard/check_last_4');
  const registerUSBankCard = usePost('/usbank/register_cardholder');
  const validateUSBankCard = usePost('/usbank/validate_registration');
  const component = JSON.parse(props.Metadata || '{}') as CardManagementType;

  const validClass = "max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md border-2 border-green-200 focus:ring-green-400 ring-green-400 focus:border-green-400";
  const invalidClass = "max-w-lg block w-full shadow-sm border-solid p-2 mt-1 mb-1 sm:max-w-xs sm:text-sm rounded-md border-2 border-red-200 focus:ring-red-400 ring-red-400 focus:border-red-400";
  const activeButton = "inline-flex items-center mr-2 mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";
  const inactiveButton = "inline-flex items-center mr-2 mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-400 bg-gray-200 focus:ring-2 focus:ring-offset-2 focus:ring-gray-500";

  const cardMgmtId = useId();

  useEffect(() => {
    if (props.info['legal_name']) {
      const names = props.info['legal_name'].trim().split(' ');
      setFirstName(names.shift());
      setLastName(names.pop());
    }
  }, [props.info]);

  async function associateCard() {
    try {
      setSaving(true);
      let response: Awaited<ReturnType<typeof checkGiveCardLast4 | typeof activateUsioCard | typeof registerUSBankCard>>;
      const targetField = props['Target Field'] || 'card_id';
      if (component.cardType === 'GiveCard') {
        response = await checkGiveCardLast4({ cardId, last4, targetField });
      } else if (typeof component.cardType === 'object' && component.cardType.kind === 'USBank') {
        if (!props.uid) {
          toast({ description: 'Missing configuration', variant: 'error'});
          return;
        }

        response = await registerUSBankCard({
          cardId: cardId,
          name: { first: firstName,  last: lastName },
          dob: props.info[component.cardType?.dobField || 'birth_date'],
          address: props.info[component.cardType?.addressField || 'address'],
          ssn: props.info[component.cardType?.ssnField || 'ssn'],
          email: props.info['email'],
          phone: props.info['phone_number'],
          applicant: props.uid,
          targetField: targetField
        });
      } else {
        response = await activateUsioCard({
          cardId: cardId,
          last4: last4,
          pin: pin,
          name: props.info['legal_name'],
          email: props.info['email'] || props.info['email1'] || '',
          phone: props.info['phone'] || props.info['phone_number'] || '',
          birth_date: props.info['birth_date'],
          address: props.info['home_address'] || '',
        });
      }

      if ((response as any).value && !(response as any).value?.error) {
        props.setInfoKey(targetField, cardId, true, false);
      } else {
        if ((response as any).value?.error) setErrorMsg((response as any).value.error);
        setReadyToSubmit(false);
      }
    } finally {
      setSaving(false);
    }
  }

  const cardType = useMemo(() => {
    const type = (typeof component.cardType === 'object') ? component.cardType.kind : component.cardType;
    return type || 'USIO';
  }, [component.cardType])

  useAsyncEffect(async () => {
    const expectedCardIdLength = cardType === 'USIO' ? 7 : 10;
    let ready = true;
    setErrorMsg('');

    if (cardId.length !== expectedCardIdLength) ready = false;
    if (cardType !== 'USBank' && last4.length !== 4) ready = false;
    if (cardType === 'USIO' && (pin.length !== 4 || pinAgain.length !== 4 || pin !== pinAgain)) ready = false;

    if (ready && cardType === 'USBank') {
      const errMsgMap: Record<string, string> = {
        "card_not_recognized": L.card.not_recognized,
        "ineligible": L.card.ineligible_card
      };
      setValidating(true);
      const response = await validateUSBankCard({
        cardId: cardId
      });
      if ((response as any).value?.error) {
        setErrorMsg(errMsgMap[`${(response as any).value?.error}`] || L.card.validation_error);
        ready = false;
      }
 
    }

    setValidating(false);
    setReadyToSubmit(ready);
  }, [cardType, cardId, last4, pin, pinAgain]);

  return <>
    <label htmlFor={"card-id-" + cardMgmtId}>Card ID</label>
    <div className="flex flex-row items-center">
      <input 
        id={"card-id-" + cardMgmtId}
        value={cardId}
        onChange={(e) => {
          let value = e.target.value;
          if (cardType === 'GiveCard') {
            value = (e.target.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
          } else if (cardType === 'USBank') {
            value = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 10);
          } else {
            value = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 7);
          }
          setCardId(value);
        }}
        className={cardId.match(
          cardType === 'GiveCard' 
            ? /[A-Z0-9]{10}/ 
            : cardType === 'USBank' 
              ? /[0-9]{10}/ 
              : /[0-9]{7}/
        ) ? validClass : invalidClass} />
      {validating && <SpacedSpinner className="ml-2" />}
    </div>
    {cardType !== 'USBank' &&
    <>
      <label htmlFor={"card-last-4-" + cardMgmtId}>Last 4 of Card Number</label>
      <input id={"card-last-4-" + cardMgmtId} value={last4} onChange={(e) => { const value = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 4); setLast4(value); }}
        className={last4.match(/[0-9]{4}/) ? validClass : invalidClass} />
    </>
    }    
    {/* Note: we may want to reenable PIN setting for GiveCard in the future, but for now (06/22/2023) GiveCard themselves are handling it */}
    {cardType === 'USIO' &&
      <>
        <label htmlFor={"card-pin-" + cardMgmtId}>PIN</label>
        <input id={"card-pin-" + cardMgmtId} type='password' value={pin} onChange={(e) => { const value = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 4); setPin(value); }}
          className={pin.match(/[0-9]{4}/) ? validClass : invalidClass} />
        <label htmlFor={"card-pin-confirm-" + cardMgmtId}>PIN Again</label>
        <input id={"card-pin-confirm-" + cardMgmtId} type='password' value={pinAgain} onChange={(e) => { const value = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 4); setPinAgain(value); }}
          className={pinAgain.match(/[0-9]{4}/) && pin === pinAgain ? validClass : invalidClass} />
        {pinAgain.match(/[0-9]{4}/) && pin !== pinAgain && <div className="text-red-500">PINs do not match</div>}
      </>
    }
    {typeof component.cardType === 'object' && component.cardType.kind === 'USBank' && readyToSubmit &&
      <div className="p-3 my-3 bg-gray-50 rounded-lg">
        <div className="flex flex-row items-center mb-2"><ExclamationTriangleIcon className="h-7 w-7 text-yellow-400 mr-2 flex-shrink-0" aria-hidden="true" /> 
          <span className="flex-wrap">
            {L.card.will_be_registered} <span className="font-bold">{L.card.verify_correct}</span>
          </span>
        </div>
        <div className="pl-2 mt-1 text-sm">
          <div><span className="font-bold">{L.card.first_name}</span>{firstName}</div>
          <div><span className="font-bold">{L.card.last_name}</span>{lastName}</div>
          <div><span className="font-bold">{L.card.dob}</span>{props.info[component.cardType?.dobField || 'birth_date']}</div>
          <div><span className="font-bold">{L.card.address}</span>{props.info[component.cardType?.addressField || 'address']}</div>
          <div><span className="font-bold">{L.card.email}</span>{props.info[component.cardType?.emailField || 'email']}</div>
          <div><span className="font-bold">{L.card.phone}</span>{props.info['phone_number']}</div>
        </div>
      </div>
    }
    {!readyToSubmit && !!errorMsg && 
      <div className="p-2 mt-2 bg-red-100 rounded-lg text-red-800 text-sm">{errorMsg}</div>
    }
    <button
      disabled={!readyToSubmit || saving}
      className={readyToSubmit ? activeButton : inactiveButton}
      type="submit" onClick={(e) => { e.preventDefault(); associateCard(); }}>
      {saving && <SpacedSpinner />}
      {cardType === 'USBank' 
        ? <span>{L.card.register_card}</span>
        : <span>{L.card.associate_card}</span>
      }
    </button>
  </>
}

function CardManagement(props: QuestionProps) {

  const config = useContext(ConfigurationContext);

  if ((!props['Target Field'] && props.info['card_id']) || (props["Target Field"] && (props.info[props['Target Field'] + "_id"] || props.info[props['Target Field']]))) {
    if (config.user && config.roles) {
      return <ChipCardStatus {...props} />
    }
    return <h1>Card Associated!</h1>
  } else {
    return React.createElement(ChipCardAssociation, props);
  }

}

export { CardManagement };
